import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import NetflixRow from "@/components/NetflixRow";
import SEO from "@/components/SEO";
import FAQSection from "@/components/FAQSection";
import { 
  Play, 
  Heart, 
  GraduationCap, 
  Shield, 
  Users,
  Video,
  Palette,
  Camera,
  Globe,
  MessageSquare,
  Lightbulb,
  ArrowRight
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const HomePage = () => {
  const [caseStudies, setCaseStudies] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchCaseStudies();
  }, []);

  const fetchCaseStudies = async () => {
    try {
      const response = await axios.get(`${API}/case-studies?featured=true`);
      setCaseStudies(response.data);
    } catch (error) {
      console.error("Error fetching case studies:", error);
    } finally {
      setLoading(false);
    }
  };

  const serviceCategories = [
    {
      id: "values",
      title: "Staff Engagement",
      description: "Unite your workforce under a shared vision with compelling culture campaigns.",
      image: "https://images.unsplash.com/photo-1552664730-d307ca884978?w=800",
      href: "/staff-engagement",
      icon: Heart,
    },
    {
      id: "training",
      title: "Training",
      description: "Transform onboarding and upskilling with professional video and digital content.",
      image: "https://images.unsplash.com/photo-1581091226825-a6a2a5aee158?w=800",
      href: "/training",
      icon: GraduationCap,
    },
    {
      id: "health-safety",
      title: "Health & Safety",
      description: "Engage your workforce in safety culture with impactful training materials.",
      image: "https://images.unsplash.com/photo-1504307651254-35680f356dfd?w=800",
      href: "/health-safety",
      icon: Shield,
    },
    {
      id: "recruitment",
      title: "Recruitment",
      description: "Attract top talent with authentic employer brand campaigns.",
      image: "https://images.unsplash.com/photo-1521791136064-7986c2920216?w=800",
      href: "/recruitment",
      icon: Users,
    },
  ];

  const capabilities = [
    { icon: Lightbulb, title: "Strategy & Development", description: "From concept to execution" },
    { icon: Video, title: "Video Production", description: "Filming, editing & motion graphics" },
    { icon: Palette, title: "Graphic Design", description: "Print & digital assets" },
    { icon: Camera, title: "Photography", description: "Professional on-site shoots" },
    { icon: MessageSquare, title: "Content Creation", description: "Social media & copywriting" },
    { icon: Globe, title: "Website Development", description: "Portals & microsites" },
  ];

  const stats = [
    { value: "500+", label: "Projects Delivered" },
    { value: "98%", label: "Client Satisfaction" },
    { value: "50+", label: "Industries Served" },
    { value: "10+", label: "Years Experience" },
  ];

  return (
    <div data-testid="home-page" className="min-h-screen">
      <SEO
        title="Internal Communications Agency for Large Organisations | Strategy, Video & Design"
        description="Specialist internal communications agency helping organisations with large workforces improve staff retention, onboarding, training and employee engagement through strategy, video production, graphic design and content creation."
        keywords="internal communications, internal comms, internal comms agency, employee engagement, corporate communications, employer branding, internal communications strategy, staff retention, employee onboarding, large organisations"
        canonical="/"
      />
      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center" data-testid="hero-section">
        {/* Background */}
        <div className="absolute inset-0 z-0">
          <img
            src="https://customer-assets.emergentagent.com/job_2f219309-49d2-4096-9b1f-9aa2825973b1/artifacts/2fu1l8do_video%20production%20liverpool%20-%20shaun.jpg"
            alt="Internal communications video production team reviewing footage on set"
            className="w-full h-full object-cover object-[center_20%]"
            decoding="async"
            fetchpriority="high"
          />
          <div className="absolute inset-0 bg-black/50" />
          <div className="absolute inset-0 hero-overlay" />
        </div>

        {/* Content */}
        <div className="container mx-auto px-4 md:px-8 relative z-10 pt-32">
          <div className="max-w-4xl">
            <div className="animate-fade-in-up opacity-0" style={{ animationDelay: "0.1s", animationFillMode: "forwards" }}>
              <span className="inline-block px-4 py-2 mb-6 text-sm font-semibold uppercase tracking-wider rounded-sm bg-[#00E5FF]/10 text-[#00E5FF] border border-[#00E5FF]/20">
                Internal Communications Specialists
              </span>
            </div>

            <h1 
              className="font-display text-5xl md:text-7xl lg:text-8xl tracking-tight leading-none text-white mb-6 animate-fade-in-up opacity-0"
              style={{ animationDelay: "0.2s", animationFillMode: "forwards" }}
            >
              CONNECT YOUR BRAND
              <br />
              <span className="text-[#00E5FF]">WITH YOUR PEOPLE</span>
            </h1>

            <p 
              className="text-lg md:text-xl text-[#B3B3B3] max-w-2xl mb-8 animate-fade-in-up opacity-0"
              style={{ animationDelay: "0.3s", animationFillMode: "forwards" }}
            >
              We create powerful internal communications for organisations with large, complex workforces — improving staff retention, strengthening culture, and transforming how your people connect with your brand. From values campaigns to safety training, onboarding to recruitment, we handle everything.
            </p>

            <div 
              className="flex flex-col sm:flex-row gap-4 animate-fade-in-up opacity-0"
              style={{ animationDelay: "0.4s", animationFillMode: "forwards" }}
            >
              <Link to="/contact">
                <Button
                  data-testid="hero-book-call-btn"
                  className="bg-[#00E5FF] text-black hover:bg-[#00B8CC] font-bold px-8 py-6 uppercase tracking-wider text-base"
                >
                  <Play className="w-5 h-5 mr-2 fill-black" />
                  Book a Call
                </Button>
              </Link>
              <Link to="/case-studies">
                <Button
                  data-testid="hero-view-work-btn"
                  variant="outline"
                  className="bg-white/10 text-white hover:bg-white/20 border-white/10 px-8 py-6 uppercase tracking-wider text-base"
                >
                  View Our Work
                </Button>
              </Link>
            </div>
          </div>
        </div>

        {/* Scroll indicator */}
        <div className="absolute bottom-8 left-1/2 -translate-x-1/2 animate-bounce">
          <div className="w-6 h-10 rounded-full border-2 border-white/30 flex justify-center pt-2">
            <div className="w-1.5 h-3 bg-[#00E5FF] rounded-full" />
          </div>
        </div>
      </section>

      {/* Services Section - Netflix Style */}
      <section className="py-6 md:py-8 bg-[#050505]" data-testid="services-section">
        <div className="container mx-auto px-4 md:px-8">
          <div className="mb-12">
            <h2 className="font-display text-4xl md:text-5xl tracking-wide text-white mb-4">
              OUR SERVICES
            </h2>
            <p className="text-[#B3B3B3] text-lg max-w-2xl">
              Four pillars of internal communications excellence. Each delivered with 
              strategic thinking, creative execution, and measurable results.
            </p>
          </div>

          <div className="flex flex-wrap justify-center gap-6">
            {serviceCategories.map((item) => (
              <Link key={item.id} to={item.href} className="flex-shrink-0">
                <div className="relative w-[300px] md:w-[340px] aspect-video bg-[#141414] rounded-md overflow-hidden netflix-card border border-transparent group">
                  <img
                    src={item.image}
                    alt={item.title}
                    className="w-full h-full object-cover"
                    loading="lazy"
                    decoding="async"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-[#050505] via-transparent to-transparent" />
                  
                  {/* Always visible overlay */}
                  <div className="absolute inset-0 bg-[#050505]/80 flex flex-col justify-end p-6">
                    <div className="flex items-center gap-3 mb-3">
                      <div className="w-10 h-10 rounded-full bg-[#00E5FF] flex items-center justify-center">
                        <Play className="w-5 h-5 text-black fill-black" />
                      </div>
                      <span className="text-sm text-[#00E5FF] font-semibold uppercase tracking-wider">
                        Learn More
                      </span>
                    </div>
                    <h4 className="font-display text-xl md:text-2xl text-white tracking-wide mb-2">
                      {item.title}
                    </h4>
                    <p className="text-[#B3B3B3] text-sm line-clamp-2">{item.description}</p>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Problem/Solution Section */}
      <section className="py-6 md:py-8 bg-[#0a0a0a]" data-testid="problem-solution-section">
        <div className="container mx-auto px-4 md:px-8">
          <div className="max-w-4xl mx-auto text-center mb-16">
            <h2 className="font-display text-4xl md:text-5xl tracking-wide text-white mb-6">
              SOUND FAMILIAR?
            </h2>
            <p className="text-[#B3B3B3] text-lg max-w-3xl mx-auto">
              Most organisations already have the training, policies and values in place. The problem? 
              Nobody sees them. We bridge the gap between what your company has and what your people actually know.
            </p>
          </div>

          {/* Relatable scenarios */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 max-w-6xl mx-auto mb-16">
            {[
              "Your intranet has hundreds of resources but nobody uses it",
              "You spent thousands on training that sits unwatched",
              "Staff say they don't know what's going on",
              "New starters feel lost for their first 3 months",
            ].map((scenario, index) => (
              <div key={index} className="p-5 bg-[#141414] rounded-lg border border-[#1F1F1F] hover:border-[#00E5FF]/30 transition-colors">
                <p className="text-white text-sm leading-relaxed">"{scenario}"</p>
              </div>
            ))}
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-5xl mx-auto">
            {/* Problems */}
            <div className="p-8 bg-[#141414] rounded-lg border border-[#00E5FF]/20">
              <h3 className="font-display text-2xl text-[#00E5FF] mb-6">
                THE CHALLENGES YOU FACE
              </h3>
              <ul className="space-y-4">
                {[
                  "Staff feel disconnected from company values",
                  "Training materials exist but nobody knows where to find them",
                  "Company values are on the wall but not in the culture",
                  "Important updates get lost in email chains",
                  "New starters take months to feel connected",
                  "Safety compliance is a constant struggle",
                  "Recruitment campaigns fail to attract talent",
                  "No internal resource to produce quality content",
                ].map((problem, index) => (
                  <li key={index} className="flex items-start gap-3 text-[#B3B3B3]">
                    <span className="text-[#00E5FF] mt-1">{"\u2715"}</span>
                    {problem}
                  </li>
                ))}
              </ul>
            </div>

            {/* Solutions */}
            <div className="p-8 bg-[#141414] rounded-lg border border-[#00E5FF]/20">
              <h3 className="font-display text-2xl text-[#00E5FF] mb-6">
                HOW WE SOLVE THEM
              </h3>
              <ul className="space-y-4">
                {[
                  "Compelling values campaigns that resonate and stick",
                  "Training content people can actually find and want to watch",
                  "Values brought to life through real stories, not posters",
                  "Clear, engaging comms that cut through the noise",
                  "Onboarding programmes that get people up to speed fast",
                  "Safety content that genuinely changes behaviour",
                  "Employer branding that attracts the right talent",
                  "One agency for all your internal comms needs",
                ].map((solution, index) => (
                  <li key={index} className="flex items-start gap-3 text-[#B3B3B3]">
                    <span className="text-[#00E5FF] mt-1">{"\u2713"}</span>
                    {solution}
                  </li>
                ))}
              </ul>
            </div>
          </div>

          {/* The Real Problem */}
          <div className="max-w-4xl mx-auto mt-16 p-8 bg-[#0a0a0a] border border-[#00E5FF]/10 rounded-lg text-center">
            <h3 className="font-display text-2xl md:text-3xl text-white mb-4">
              THE REAL PROBLEM
            </h3>
            <p className="text-[#B3B3B3] text-lg leading-relaxed">
              It's rarely a lack of content. Most organisations have policies, training materials, values documents 
              and company updates already created. The real problem is that none of it reaches the people who need it. 
              It sits on shared drives, buried in intranets, or lost in all-staff emails that nobody reads. 
              We take what you already have — and what you need to create — and make sure every employee 
              actually sees it, understands it, and acts on it.
            </p>
          </div>
        </div>
      </section>

      {/* What We Do Section */}
      <section className="py-6 md:py-8 bg-[#0a0a0a]" data-testid="capabilities-section">
        <div className="container mx-auto px-4 md:px-8">
          <div className="text-center mb-16">
            <h2 className="font-display text-4xl md:text-5xl tracking-wide text-white mb-4">
              EVERYTHING YOU NEED
            </h2>
            <p className="text-[#B3B3B3] text-lg max-w-2xl mx-auto">
              From conception to implementation, we handle every aspect of your 
              internal communications. No outsourcing, no gaps, just results.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {capabilities.map((cap, index) => (
              <div
                key={index}
                data-testid={`capability-${cap.title.toLowerCase().replace(/\s+/g, '-')}`}
                className="group p-8 bg-[#141414] rounded-lg border border-[#1F1F1F] hover:border-[#00E5FF]/30 transition-colors"
              >
                <div className="w-14 h-14 rounded-lg bg-[#00E5FF]/10 flex items-center justify-center mb-6 group-hover:bg-[#00E5FF]/20 transition-colors">
                  <cap.icon className="w-7 h-7 text-[#00E5FF]" />
                </div>
                <h3 className="font-display text-xl tracking-wide text-white mb-2">
                  {cap.title}
                </h3>
                <p className="text-[#B3B3B3]">{cap.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Case Studies Section */}
      <section className="py-6 md:py-8 bg-[#0a0a0a]" data-testid="case-studies-section">
        <div className="container mx-auto px-4 md:px-8">
          <div className="mb-12 flex flex-col md:flex-row md:items-end md:justify-between gap-6">
            <div>
              <h2 className="font-display text-4xl md:text-5xl tracking-wide text-white mb-4">
                FEATURED WORK
              </h2>
              <p className="text-[#B3B3B3] text-lg max-w-2xl">
                Real results for real businesses. See how we've transformed internal 
                communications across industries.
              </p>
            </div>
            <Link to="/case-studies">
              <Button
                data-testid="view-all-case-studies-btn"
                variant="outline"
                className="bg-transparent text-white hover:bg-white/10 border-[#333] hover:border-[#00E5FF]/50"
              >
                View All Case Studies
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </Link>
          </div>

          {loading ? (
            <div className="flex justify-center py-12">
              <div className="spinner" />
            </div>
          ) : (
            <div className="flex flex-wrap justify-center gap-6">
              {caseStudies.map((study) => (
                <Link key={study.id} to={`/case-studies/${study.id}`} className="flex-shrink-0">
                  <div className="relative w-[300px] md:w-[400px] aspect-video bg-[#141414] rounded-md overflow-hidden netflix-card border border-transparent group">
                    <img
                      src={study.thumbnail}
                      alt={study.title}
                      className="w-full h-full object-cover"
                      loading="lazy"
                      decoding="async"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-[#050505] via-transparent to-transparent" />
                    
                    {/* Category Badge */}
                    <div className="absolute top-4 left-4">
                      <span className="px-3 py-1 text-xs font-semibold uppercase tracking-wider rounded-full bg-[#00E5FF]/20 text-[#00E5FF] border border-[#00E5FF]/30">
                        {study.category?.replace("_", " ")}
                      </span>
                    </div>

                    {/* Hover Overlay */}
                    <div className="absolute inset-0 bg-[#050505]/90 opacity-0 group-hover:opacity-100 transition-opacity flex flex-col justify-end p-6">
                      <div className="flex items-center gap-3 mb-3">
                        <div className="w-10 h-10 rounded-full bg-[#00E5FF] flex items-center justify-center">
                          <Play className="w-5 h-5 text-black fill-black" />
                        </div>
                        <span className="text-sm text-[#00E5FF] font-semibold uppercase tracking-wider">
                          View Case Study
                        </span>
                      </div>
                      <span className="text-[#B3B3B3] text-xs uppercase tracking-wider mb-1">
                        {study.client}
                      </span>
                      <h4 className="font-display text-lg md:text-xl text-white tracking-wide mb-2">
                        {study.title}
                      </h4>
                      <p className="text-[#B3B3B3] text-sm line-clamp-2">{study.summary}</p>
                    </div>

                    {/* Default Content */}
                    <div className="absolute bottom-0 left-0 right-0 p-4 group-hover:opacity-0 transition-opacity">
                      <span className="text-[#B3B3B3] text-xs uppercase tracking-wider">
                        {study.client}
                      </span>
                      <h4 className="font-display text-base md:text-lg text-white tracking-wide mt-1">
                        {study.title}
                      </h4>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* FAQ Section */}
      <FAQSection faqs={[
        { question: "Why do large companies need an internal comms agency?", answer: "Organisations with large, dispersed workforces often struggle to communicate consistently across departments, sites and teams. An internal comms agency provides the strategy, creative capability and production expertise to ensure every employee receives clear, engaging communications — without overloading your internal team." },
        { question: "How does internal communications improve staff retention?", answer: "Employees who feel informed, valued and connected to their organisation's purpose are significantly more likely to stay. Effective internal communications builds that connection through values campaigns, regular updates, recognition content and authentic storytelling that makes people feel part of something bigger." },
        { question: "What's the difference between internal and external communications?", answer: "External communications targets customers and the public. Internal communications focuses entirely on your workforce — your employees, contractors and stakeholders. The tone, channels and objectives are completely different, which is why specialist internal comms expertise delivers better results than repurposing external marketing." },
        { question: "Can you work alongside our existing HR and comms teams?", answer: "Absolutely. We operate as an extension of your team, not a replacement. We work alongside HR, internal comms, L&D and leadership teams to deliver strategy, content and campaigns that complement what you're already doing — filling capability gaps and increasing output." },
        { question: "What services does an internal comms agency provide?", answer: "We provide end-to-end internal communications including strategy development, video production, graphic design, professional photography, content creation, social media management and website/portal development — all tailored specifically for employee audiences." }
      ]} />

      {/* CTA Section */}
      <section className="py-6 md:py-8 bg-[#0a0a0a] relative overflow-hidden" data-testid="cta-section">
        <div className="absolute inset-0 opacity-20">
          <div className="absolute top-0 right-0 w-96 h-96 bg-[#00E5FF] rounded-full blur-[150px]" />
        </div>
        
        <div className="container mx-auto px-4 md:px-8 relative z-10">
          <div className="max-w-3xl mx-auto text-center">
            <h2 className="font-display text-4xl md:text-6xl tracking-wide text-white mb-6">
              READY TO TRANSFORM YOUR
              <br />
              <span className="text-[#00E5FF]">INTERNAL COMMS?</span>
            </h2>
            <p className="text-[#B3B3B3] text-lg mb-8">
              Let's discuss how we can help unite your people, drive your culture, 
              and create communications that actually work.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/contact">
                <Button
                  data-testid="cta-book-call-btn"
                  className="bg-[#00E5FF] text-black hover:bg-[#00B8CC] font-bold px-8 py-6 uppercase tracking-wider text-base"
                >
                  Book a Call
                </Button>
              </Link>
              <Link to="/contact">
                <Button
                  data-testid="cta-contact-btn"
                  variant="outline"
                  className="bg-white/10 text-white hover:bg-white/20 border-white/10 px-8 py-6 uppercase tracking-wider text-base"
                >
                  Get in Touch
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default HomePage;
